import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import { Link } from "react-router-dom";
import Table from "../../../../core/common/dataTable";
import { getNatureOfDutyList } from "../../../../core/data/redux/outsideJobNatureSlice";
import { all_routes } from "../../../router/all_routes";

type AppDispatch = ThunkDispatch<any, any, AnyAction>;

export interface OutsideJobNatureType {
  id: number;
  nature_of_duty_id: string;
  nature_of_duty_title: string;
  sapCreatedAt?: string;
  sapModifiedAt?: string;
}

interface Pagination {
  current: number;
  pageSize: number;
  total: number;
}

interface SearchParams {
  query: string;
}

const OutsideJobNature = () => {
  const dispatch = useDispatch<AppDispatch>();
  const natureOfDutyState = useSelector((state: any) => state.natureOfDutyState);
  const routes = all_routes;

  const [dataList, setDataList] = useState<OutsideJobNatureType[]>([]);
  const [searchParams, setSearchParams] = useState<SearchParams>({ query: "" });
  const [pagination, setPagination] = useState<Pagination>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchRecords = useCallback(
    (searchParams: SearchParams, page: number, pageSize: number) => {
      dispatch(getNatureOfDutyList({ query: searchParams.query, page, limit: pageSize }));
    },
    [dispatch]
  );

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchRecords(searchParams, pagination.current, pagination.pageSize);
    }, 500);

    return () => clearTimeout(delay);
  }, [searchParams, pagination.current, pagination.pageSize, fetchRecords]);

  useEffect(() => {
    if (Array.isArray(natureOfDutyState.data)) {
      setDataList(natureOfDutyState.data);
      setPagination((prev) => ({
        ...prev,
        total: natureOfDutyState.total || natureOfDutyState.data.length || 0,
      }));
    }
  }, [natureOfDutyState.data, natureOfDutyState.total]);

  const handlePagination = (newPagination: Pagination) => {
    setPagination(newPagination);
    fetchRecords(searchParams, newPagination.current, newPagination.pageSize);
  };

  const handleSearch = (value: string) => {
    setSearchParams({ query: value });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const getSerialNumber = (index: number) =>
    (pagination.current - 1) * pagination.pageSize + index + 1;

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 90,
      render: (_: any, __: OutsideJobNatureType, index: number) => (
        <div>{getSerialNumber(index)}</div>
      ),
    },
    {
      title: "SAP ID",
      dataIndex: "nature_of_duty_id",
      width: 120,
    },
    {
      title: "SAP Title",
      dataIndex: "nature_of_duty_title",
      width: 200,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      width: 180,
      render: (date: string) => <div>{formatDate(date)}</div>,
    },
    {
      title: "Modified At",
      dataIndex: "modified_at",
      width: 180,
      render: (date: string) => <div>{formatDate(date)}</div>,
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Nature Of Duty</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Settings</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Nature Of Duty
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-4 mt-3">Nature Of Duty</h4>
          </div>

          <div className="card-body p-0 py-3">
            <Table
              columns={columns}
              dataSource={dataList}
              Selection={false}
              paginationConfig={pagination}
              handlePagination={handlePagination}
              loading={natureOfDutyState.loading}
              handleSearch={handleSearch}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutsideJobNature;
